import { useMemo, useState } from "react";
import {
  FileText,
  Presentation,
  Calendar,
  ChevronRight,
  ChevronLeft,
  MessageSquare,
  Users,
  FileCheck,
  BarChart4,
  ClipboardList,
  ClipboardCheck,
  DollarSign,
  Ruler,
  Mail,
  CheckCircle,
  LucideIcon,
  Activity,
  FileSignature,
  Building2,
  PanelRightOpen,
  Smartphone,
} from "lucide-react";
import { cn } from "@/lib/utils";
import { ExtendedProject } from "@/types/project";
import { useDrawerStack } from "@/contexts/DrawerStackContext";
import type { DrawerType } from "@/types/drawer";
import { useContractsFeatureAccess } from "@/hooks/useContractsFeatureAccess";
import { useProposalsFeatureAccess } from "@/hooks/useProposalsFeatureAccess";
import { useCanWriteProject } from "@/hooks/usePartnerScoped";
import { canReadContracts } from "@/lib/contractAccess";

// ============================================================================
// Types & Interfaces
// ============================================================================

interface SidebarItemConfig {
  id: string;
  label: string;
  icon: LucideIcon;
  visible: boolean;
  /** If set, this item opens as a drawer instead of switching tabs */
  drawerType?: DrawerType;
}

interface PermissionChecker {
  (permission: string): boolean;
}

interface ProjectModalSidebarProps {
  activeTab: string;
  onTabChange: (tabId: string) => void;
  project: ExtendedProject | null;
  formValues: {
    pipeline_id?: number | null;
    project_type?: string;
  };
  isFormCompleted: boolean;
  sidebarCollapsed: boolean;
  onToggleSidebar: () => void;
  hasPermission: PermissionChecker;
}

// ============================================================================
// Tab ID to DrawerType mapping
// ============================================================================

/**
 * Maps sidebar tab IDs to drawer types.
 * "opportunity" is NOT mapped because it stays as a tab in the modal.
 */
const TAB_TO_DRAWER_MAP: Record<string, DrawerType> = {
  "design": "documents",
  "commercial-designs": "designs",
  "book-consultation": "consultations",
  "proposals": "proposals",
  "contracts": "contracts",
  "lender": "lender",
  "site-survey": "site-survey",
  "tasks": "project-management",
  "finance": "finance",
  "communication": "communication",
  "emails": "emails",
  "reports": "reports",
  "notes": "notes",
  "partner": "partner",
  "sales-manager": "sales-team",
  "work-orders": "work-orders",
  "project-tasks": "task", // Opens task drawer in create mode
  "timeline": "activity",
  "new-project-form": "intake-form",
};

// ============================================================================
// Helper Functions
// ============================================================================

/**
 * Builds the list of visible sidebar items based on project state and user permissions
 */
function getVisibleItems(
  project: ExtendedProject | null,
  formValues: { pipeline_id?: number | null; project_type?: string },
  isFormCompleted: boolean,
  hasPermission: PermissionChecker,
  // CB-8.1: tenant feature gate — whether the current user's role may use Contracts at all.
  hasContractsFeature: boolean,
  // EPIC-PROP-001: tenant feature gate for Proposals (Sandbox-only during rollout).
  hasProposalsFeature: boolean
): SidebarItemConfig[] {
  const pipelineId = project?.stages?.pipeline_id ?? formValues.pipeline_id;
  const stageOrder = project?.stages?.stage_order ?? 0;
  const projectType = formValues.project_type;
  const isCommercial = projectType === "Commercial" || projectType === "commercial";

  // Permission helper for common permission patterns
  const hasAdminOrOverride = () =>
    hasPermission("system.override") || hasPermission("tenant.manage");

  const hasProjectManagement = () =>
    hasPermission("manage_projects") ||
    hasPermission("project.manage_all") ||
    hasPermission("edit_projects") ||
    hasAdminOrOverride();

  const hasFinanceAccess = () =>
    hasPermission("view_finance") ||
    hasPermission("manage_finance") ||
    hasPermission("edit_finance") ||
    hasAdminOrOverride();

  // RBAC-7.3: exactly what contracts_select asks. tenant.manage is held only by Superadmin, which
  // holds system.override, so dropping it from this gate changes nobody's answer.
  const hasContractAccess = () => canReadContracts(hasPermission);

  const hasPartnerAccess = () =>
    hasPermission("project.manage_all") ||
    hasPermission("manage_partners") ||
    hasPermission("view_partners") ||
    hasPermission("edit_partners") ||
    hasAdminOrOverride();

  const hasSalesTeamAccess = () =>
    hasPermission("project.manage_all") ||
    hasPermission("manage_sales_team") ||
    hasPermission("view_sales_team") ||
    hasAdminOrOverride();

  const hasWorkOrderAccess = () =>
    hasPermission("manage_work_orders") ||
    hasPermission("view_work_orders") ||
    hasPermission("edit_work_orders") ||
    hasAdminOrOverride();

  const hasEmailAccess = () =>
    hasPermission("project.send_email") || hasAdminOrOverride();

  const hasReportsAccess = () =>
    hasPermission("settings.admin") ||
    hasPermission("project.manage_all") ||
    hasAdminOrOverride();

  const hasTasksAccess = () =>
    hasPermission("manage_projects") ||
    hasPermission("edit_projects") ||
    hasPermission("view_projects") ||
    hasAdminOrOverride();

  // Build items array with visibility conditions
  // Note: "opportunity" has no drawerType - it stays as the main modal content
  const items: SidebarItemConfig[] = [
    {
      id: "opportunity",
      label: "Project Overview",
      icon: FileText,
      visible: true,
      // No drawerType - stays as tab content
    },
    {
      id: "new-project-form",
      label: "Project Intake Form",
      icon: ClipboardCheck,
      visible: !isFormCompleted && [1, 57].includes(formValues.pipeline_id || 0),
      drawerType: TAB_TO_DRAWER_MAP["new-project-form"],
    },
    {
      id: "design",
      label: "Documents",
      icon: FileCheck,
      visible: true,
      drawerType: TAB_TO_DRAWER_MAP["design"],
    },
    {
      // EPIC-PROP-001: sits between Documents and Consultations; gated to enabled tenants
      // (Tripper's Sandbox) via the feature seam until GA.
      id: "proposals",
      label: "Proposals",
      icon: Presentation,
      visible: hasProposalsFeature,
      drawerType: TAB_TO_DRAWER_MAP["proposals"],
    },
    {
      id: "commercial-designs",
      label: "Designs",
      icon: Ruler,
      visible: isCommercial,
      drawerType: TAB_TO_DRAWER_MAP["commercial-designs"],
    },
    {
      id: "book-consultation",
      label: "Consultations",
      icon: Calendar,
      visible: true,
      drawerType: TAB_TO_DRAWER_MAP["book-consultation"],
    },
    {
      id: "contracts",
      label: "Contracts",
      icon: FileSignature,
      // Contracts tab shows wherever the tenant feature gate (hasContractsFeature) + the user's
      // contract permission allow. The old hardcoded pipeline/stage restriction ([1,517] &&
      // stageOrder>=10) was tenant-specific and hid Contracts for every other tenant — dropped so
      // Contracts works multi-tenant. (Pipeline-appropriate CONTRACT TEMPLATES are scoped
      // separately, at template selection.)
      visible: hasContractAccess() && hasContractsFeature,
      drawerType: TAB_TO_DRAWER_MAP["contracts"],
    },
    {
      id: "lender",
      label: "Lender",
      icon: Building2,
      visible: pipelineId === 1 && stageOrder >= 10 && hasContractAccess(),
      drawerType: TAB_TO_DRAWER_MAP["lender"],
    },
    {
      id: "site-survey",
      label: "Site Survey",
      icon: ClipboardList,
      visible: true,
      drawerType: TAB_TO_DRAWER_MAP["site-survey"],
    },
    {
      id: "tasks",
      label: "Project Management",
      icon: BarChart4,
      visible: hasProjectManagement(),
      drawerType: TAB_TO_DRAWER_MAP["tasks"],
    },
    {
      id: "finance",
      label: "Finance",
      icon: DollarSign,
      visible: hasFinanceAccess(),
      drawerType: TAB_TO_DRAWER_MAP["finance"],
    },
    {
      id: "communication",
      label: "Communication",
      icon: MessageSquare,
      visible: true,
      drawerType: TAB_TO_DRAWER_MAP["communication"],
    },
    {
      id: "sms",
      label: "SMS",
      icon: Smartphone,
      visible: true,
    },
    {
      id: "emails",
      label: "Emails",
      icon: Mail,
      visible: hasEmailAccess(),
      drawerType: TAB_TO_DRAWER_MAP["emails"],
    },
    {
      id: "reports",
      label: "Reports",
      icon: BarChart4,
      visible: hasReportsAccess(),
      drawerType: TAB_TO_DRAWER_MAP["reports"],
    },
    {
      id: "notes",
      label: "Project Notes",
      icon: FileText,
      visible: true,
      drawerType: TAB_TO_DRAWER_MAP["notes"],
    },
    {
      id: "partner",
      label: "Partner Info",
      icon: Users,
      visible: hasPartnerAccess(),
      drawerType: TAB_TO_DRAWER_MAP["partner"],
    },
    {
      id: "sales-manager",
      label: "Sales Team Assignments",
      icon: Users,
      visible: hasSalesTeamAccess(),
      drawerType: TAB_TO_DRAWER_MAP["sales-manager"],
    },
    {
      id: "work-orders",
      label: "Work Orders",
      icon: ClipboardList,
      visible: hasWorkOrderAccess(),
      drawerType: TAB_TO_DRAWER_MAP["work-orders"],
    },
    {
      id: "project-tasks",
      label: "Tasks",
      icon: CheckCircle,
      visible: hasTasksAccess(),
      drawerType: TAB_TO_DRAWER_MAP["project-tasks"],
    },
    {
      id: "timeline",
      label: "Activity",
      icon: Activity,
      visible: true,
      drawerType: TAB_TO_DRAWER_MAP["timeline"],
    },
  ];

  return items.filter((item) => item.visible);
}

// ============================================================================
// Subcomponents
// ============================================================================

interface SidebarItemProps {
  item: SidebarItemConfig;
  isActive: boolean;
  isDrawerOpen: boolean;
  isCollapsed: boolean;
  onTabClick: () => void;
  onDrawerClick: () => void;
}

function SidebarItem({ item, isActive, isDrawerOpen, isCollapsed, onTabClick, onDrawerClick }: SidebarItemProps) {
  const [isHovered, setIsHovered] = useState(false);
  const Icon = item.icon;
  const hasDrawer = !!item.drawerType;

  return (
    <div
      className={cn(
        "w-full flex items-center rounded-md text-left transition-all text-sm group relative overflow-hidden",
        // For items without drawers (Overview), use active state
        !hasDrawer && isActive && "bg-primary text-background font-medium",
        // For items with drawers, show active or drawer-open indicator
        hasDrawer && isActive && !isDrawerOpen && "bg-primary text-background font-medium",
        hasDrawer && isDrawerOpen && "bg-primary/10 text-primary font-medium border-l-2 border-primary",
        // Default hover state
        !isActive && !isDrawerOpen && "text-foreground hover:bg-muted",
        isCollapsed && "justify-center"
      )}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      {/* Main clickable area - switches tab */}
      <button
        type="button"
        className={cn(
          "flex-1 flex items-center pl-3 pr-1 py-2 text-left",
          isCollapsed && "justify-center"
        )}
        onClick={(e) => {
          e.preventDefault();
          e.stopPropagation();
          onTabClick();
        }}
        aria-pressed={isActive}
      >
        <Icon
          className="h-5 w-5 shrink-0 transition-transform"
          style={{
            marginRight: isCollapsed ? 0 : "0.75rem",
          }}
        />
        {!isCollapsed && (
          <span className="whitespace-nowrap">{item.label}</span>
        )}
      </button>

      {/* Drawer icon button - fixed position on right */}
      {!isCollapsed && hasDrawer && (
        <button
          type="button"
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            onDrawerClick();
          }}
          className={cn(
            "shrink-0 p-1 rounded transition-all duration-200",
            "hover:bg-black/10 dark:hover:bg-white/10",
            isDrawerOpen
              ? "opacity-100"
              : isHovered
                ? "opacity-70"
                : "opacity-0"
          )}
          title={isDrawerOpen ? `${item.label} drawer is open` : `Open ${item.label} in drawer`}
          aria-label={isDrawerOpen ? `${item.label} drawer is open` : `Open ${item.label} in drawer`}
        >
          {isDrawerOpen ? (
            <span className="w-2 h-2 rounded-full bg-primary block animate-pulse" />
          ) : (
            <PanelRightOpen className="w-4 h-4" />
          )}
        </button>
      )}
    </div>
  );
}

// ============================================================================
// Main Component
// ============================================================================

export function ProjectModalSidebar({
  activeTab,
  onTabChange,
  project,
  formValues,
  isFormCompleted,
  sidebarCollapsed,
  onToggleSidebar,
  hasPermission,
}: ProjectModalSidebarProps) {
  const { push, drawers, focusDrawer } = useDrawerStack();

  // CB-8.1: tenant feature gate for Contracts. Closed-by-default while loading (hasAccess=false)
  // so the tab never flashes in before the gate is known.
  const { hasAccess: hasContractsFeature } = useContractsFeatureAccess();
  const { enabled: hasProposalsFeature } = useProposalsFeatureAccess();

  // Build visible items based on project state and permissions
  // RBAC-3.93: a partner-scoped caller writes nothing on a project. The intake form is only a form
  // to submit, and the Tasks drawer opens straight into creating one, so neither is offered.
  const canWrite = useCanWriteProject();
  const visibleItems = useMemo(() => {
    const items = getVisibleItems(project, formValues, isFormCompleted, hasPermission, hasContractsFeature, hasProposalsFeature);
    if (canWrite) return items;
    return items
      .filter((item) => item.id !== "new-project-form")
      .map((item) => (item.id === "project-tasks" ? { ...item, drawerType: undefined } : item));
  }, [project, formValues, isFormCompleted, hasPermission, hasContractsFeature, hasProposalsFeature, canWrite]);

  // Get list of currently open drawer types for visual indicators
  // Filter out drawers that are closing (isClosing=true) so the indicator updates immediately
  const openDrawerTypes = useMemo(() => {
    return new Set(drawers.filter((d) => !d.isClosing).map((d) => d.type));
  }, [drawers]);

  // Handle tab click - switch the modal content
  const handleTabClick = (item: SidebarItemConfig) => {
    onTabChange(item.id);
  };

  // Handle drawer icon click - open as floating drawer
  const handleDrawerClick = (item: SidebarItemConfig) => {
    if (!item.drawerType) return;

    // Check if this drawer type is already open (exclude closing drawers)
    const existingDrawer = drawers.find((d) => d.type === item.drawerType && !d.isClosing);
    if (existingDrawer) {
      // Focus/pulse the existing drawer
      focusDrawer(existingDrawer.id);
      return;
    }

    // Open a new drawer
    push({
      type: item.drawerType,
      props: {
        projectId: project?.id,
        project: project,
      },
    });
  };

  return (
    <div
      className={cn(
        "transition-all duration-300 border-r bg-muted flex flex-col relative overflow-y-auto",
        sidebarCollapsed ? "w-14" : "w-60"
      )}
    >
      {/* Bottom buttons container */}
      <div className="absolute bottom-1 right-1 flex items-center gap-1 z-10">
        {/* Sidebar collapse toggle button */}
        <button
          onClick={onToggleSidebar}
          className="p-1.5 rounded-full bg-background text-muted-foreground hover:bg-primary/20 hover:text-primary transition-colors shadow-md"
          title={sidebarCollapsed ? "Expand sidebar" : "Collapse sidebar"}
          aria-label={sidebarCollapsed ? "Expand sidebar" : "Collapse sidebar"}
        >
          {sidebarCollapsed ? (
            <ChevronRight className="h-4 w-4" />
          ) : (
            <ChevronLeft className="h-4 w-4" />
          )}
        </button>
      </div>

      {/* Scrollable navigation area */}
      <div className="flex-1 overflow-y-auto">
        <nav className="flex flex-col p-2">
          <div className="space-y-0.5">
            {visibleItems.map((item) => (
              <SidebarItem
                key={item.id}
                item={item}
                isActive={activeTab === item.id}
                isDrawerOpen={item.drawerType ? openDrawerTypes.has(item.drawerType) : false}
                isCollapsed={sidebarCollapsed}
                onTabClick={() => handleTabClick(item)}
                onDrawerClick={() => handleDrawerClick(item)}
              />
            ))}
          </div>
        </nav>
      </div>
    </div>
  );
}

ProjectModalSidebar.displayName = "ProjectModalSidebar";
